import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangleIcon,
  CopyIcon,
  PencilIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { useMemo, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  exploratoryArtifactQueryOptions,
  isExploratoryAmendment,
  isManualPublicationRequired,
  projectQueryOptions,
  useConfirmExploratoryPublication,
  useReconcileExploratoryPublication,
  useRetryExploratoryPublication,
  useReviseExploratoryArtifact,
  type ExploratoryAmendmentDraft,
  type ExploratoryDraftCase,
  type ExploratoryNewCaseDraft,
  type ExploratoryPublicationAttempt,
  type ExploratoryRevision,
} from '@/lib/api/quality.js';
import { notify } from '@/lib/notify.js';

const connectors = ['testrail', 'zephyr', 'jira', 'azure', 'xray'] as const;

/** One field's before/after comparison in an amendment proposal. */
export type ExploratoryAmendmentFieldDiff = {
  key: string;
  before: unknown;
  after: unknown;
  kind: 'added' | 'removed' | 'changed' | 'unchanged';
};

function stableJson(value: unknown): string {
  if (Array.isArray(value))
    return `[${value.map((item) => stableJson(item)).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${stableJson((value as Record<string, unknown>)[key])}`,
      )
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? String(value);
}

export function isDraftCaseArray(
  value: unknown,
): value is ExploratoryDraftCase[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item),
    )
  );
}

/**
 * Builds a field-level diff between an amendment's `before` and `after`
 * blobs. Key order is deterministic: `before`'s own key order first, then
 * any `after`-only (added) keys in `after`'s order.
 */
export function amendmentFieldDiff(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): ExploratoryAmendmentFieldDiff[] {
  const hasOwn = (record: Record<string, unknown>, key: string): boolean =>
    Object.prototype.hasOwnProperty.call(record, key);
  const keys = [
    ...Object.keys(before),
    ...Object.keys(after).filter((key) => !hasOwn(before, key)),
  ];
  return keys.map((key) => {
    const hasBefore = hasOwn(before, key);
    const hasAfter = hasOwn(after, key);
    const kind: ExploratoryAmendmentFieldDiff['kind'] = !hasBefore
      ? 'added'
      : !hasAfter
        ? 'removed'
        : stableJson(before[key]) === stableJson(after[key])
          ? 'unchanged'
          : 'changed';
    return {
      key,
      before: hasBefore ? before[key] : undefined,
      after: hasAfter ? after[key] : undefined,
      kind,
    };
  });
}

/** Renders a diff-entry value for display: a plain string as-is, anything else as JSON. */
function formatDiffValue(value: unknown): string {
  if (value === undefined) return '(none)';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

/**
 * Hand-entry copy text for an amendment proposal: the target case, then
 * every changed or added field's new value, with removals named explicitly.
 * Unchanged fields are omitted — they need no manual re-entry.
 */
export function amendmentCopyText(draft: ExploratoryAmendmentDraft): string {
  const lines = [`Target case: ${draft.target.caseRef}`];
  for (const entry of amendmentFieldDiff(draft.before, draft.after)) {
    if (entry.kind === 'unchanged') continue;
    if (entry.kind === 'removed') {
      lines.push(
        `${entry.key}: (remove, was ${formatDiffValue(entry.before)})`,
      );
    } else {
      lines.push(`${entry.key}: ${formatDiffValue(entry.after)}`);
    }
  }
  return lines.join('\n');
}

/** Copy text for either draft kind, dispatched on `proposalType`. */
function draftCopyText(draft: ExploratoryDraftCase): string {
  return isExploratoryAmendment(draft)
    ? amendmentCopyText(draft)
    : copyableDraft(draft);
}

export function hasExactPublicationDestination(
  operation: 'create' | 'update',
  destination: {
    connector: string;
    projectRef: string;
    containerRef: string;
    caseRef: string;
  },
): boolean {
  return Boolean(
    destination.connector.trim() &&
    destination.projectRef.trim() &&
    destination.containerRef.trim() &&
    (operation === 'create' || destination.caseRef.trim()),
  );
}

export function canRetryExploratoryPublication(state: string): boolean {
  return state === 'failed';
}

async function copy(text: string): Promise<void> {
  if (!navigator.clipboard?.writeText) {
    notify.error('Clipboard access is unavailable.');
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    notify.success('Copied to clipboard.');
  } catch {
    notify.error('Could not copy to clipboard.');
  }
}

function copyableDraft(draft: Record<string, unknown>): string {
  return Object.entries(draft)
    .map(
      ([key, value]) =>
        `${key}: ${typeof value === 'string' ? value : JSON.stringify(value, null, 2)}`,
    )
    .join('\n');
}

/** Small caps label above a card section — shared by both draft kinds so they read as siblings. */
function SectionLabel({ children }: { children: string }): JSX.Element {
  return (
    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * Renders a new-case draft as a reviewable case rather than its raw copy text: a labelled
 * precondition (omitted when empty, never a dangling label), numbered steps with the action and
 * expected result as two distinct pieces, and a labelled expected result. Nothing here is
 * height-clamped — the card grows with its content so nothing is truncated mid-value. The full
 * field set (including anything not shown here, e.g. custom fields or refs) stays available
 * through "Copy fields".
 */
function NewCaseDraftBody({
  draft,
}: {
  draft: ExploratoryNewCaseDraft;
}): JSX.Element {
  const precondition =
    typeof draft.precondition === 'string' ? draft.precondition.trim() : '';
  const expectedResult =
    typeof draft.expected_result === 'string'
      ? draft.expected_result.trim()
      : '';
  const steps = Array.isArray(draft.test_steps_separated)
    ? draft.test_steps_separated
    : [];
  return (
    <div className="mt-2 space-y-3 text-[11px]">
      {precondition && (
        <div>
          <SectionLabel>Precondition</SectionLabel>
          <p className="mt-1 whitespace-pre-wrap">{precondition}</p>
        </div>
      )}
      {steps.length > 0 && (
        <div>
          <SectionLabel>Steps</SectionLabel>
          <ol className="mt-1 list-decimal space-y-2 pl-4">
            {steps.map((step, stepIndex) => (
              <li key={stepIndex}>
                <p className="whitespace-pre-wrap">{step.content}</p>
                {step.expected && (
                  <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">
                    Expected: {step.expected}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
      {expectedResult && (
        <div>
          <SectionLabel>Expected result</SectionLabel>
          <p className="mt-1 whitespace-pre-wrap">{expectedResult}</p>
        </div>
      )}
    </div>
  );
}

export function ExploratoryArtifactPanel({
  runId,
  projectId,
}: {
  runId: string;
  projectId?: string | null;
}): JSX.Element | null {
  const query = useQuery(exploratoryArtifactQueryOptions(runId));
  const projectQuery = useQuery(projectQueryOptions(projectId ?? ''));
  const artifact = query.data;
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editingRevisionId, setEditingRevisionId] = useState<string | null>(
    null,
  );
  const [confirmingRevisionId, setConfirmingRevisionId] = useState<
    string | null
  >(null);
  const [draftText, setDraftText] = useState('');
  const [storyRefs, setStoryRefs] = useState('');
  const [caseRefs, setCaseRefs] = useState('');
  const [connector, setConnector] =
    useState<(typeof connectors)[number]>('testrail');
  const [operation, setOperation] = useState<'create' | 'update'>('create');
  const [projectRef, setProjectRef] = useState('');
  const [containerRef, setContainerRef] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [lockedCaseRef, setLockedCaseRef] = useState(false);
  const [draftCaseIndex, setDraftCaseIndex] = useState<number | null>(null);
  const [reconcilingAttempt, setReconcilingAttempt] =
    useState<ExploratoryPublicationAttempt | null>(null);
  const [manualOutcome, setManualOutcome] = useState<
    'published' | 'not_published'
  >('published');
  const [manualExternalCaseRef, setManualExternalCaseRef] = useState('');
  const current = artifact?.revisions.at(-1);
  const revise = useReviseExploratoryArtifact(artifact?.artifact.id ?? '');
  const confirm = useConfirmExploratoryPublication(artifact?.artifact.id ?? '');
  const retry = useRetryExploratoryPublication(artifact?.artifact.id ?? '');
  const reconcile = useReconcileExploratoryPublication(
    artifact?.artifact.id ?? '',
  );
  const availableConnectors = connectors.filter((candidate) =>
    projectQuery.data?.source_links.some((link) => link.source === candidate),
  );
  const connectorUnavailable =
    !projectId || !projectQuery.isSuccess || availableConnectors.length === 0;
  const connectorUnavailableMessage = !projectId
    ? 'This run has no project context, so connector actions are unavailable.'
    : projectQuery.isPending
      ? 'Loading connector configuration…'
      : projectQuery.isError
        ? 'Couldn’t load connector configuration. Copy-ready local drafts remain available.'
        : 'No supported connector is configured for this workspace. Copy-ready local drafts remain available.';
  const hasExplicitLinks = Boolean(
    current?.explicit_links &&
    Object.values(current.explicit_links).some((value) =>
      Array.isArray(value)
        ? value.length > 0
        : Boolean(
            value && typeof value === 'object' && Object.keys(value).length > 0,
          ),
    ),
  );
  const manualCopy = useMemo(
    () => current?.draft_cases.map(draftCopyText).join('\n\n---\n\n') ?? '',
    [current],
  );

  if (query.isLoading) return null;
  if (query.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertTriangleIcon className="size-4 text-destructive" />
            Exploratory artifacts unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>Couldn’t load the exploratory artifacts. Try again.</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            <RefreshCwIcon /> {query.isFetching ? 'Retrying…' : 'Retry'}
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!artifact || !current) return null;

  const exactDestination = hasExactPublicationDestination(operation, {
    connector,
    projectRef,
    containerRef,
    caseRef,
  });

  const beginEdit = (): void => {
    setEditingRevisionId(current.id);
    setDraftText(JSON.stringify(current.draft_cases, null, 2));
    setStoryRefs(current.manual_refs.manualStoryRefs.join(', '));
    setCaseRefs(current.manual_refs.manualCaseRefs.join(', '));
    setEditing(true);
  };
  const saveEdit = (): void => {
    if (!editingRevisionId) return;
    let draftCases: ExploratoryDraftCase[];
    try {
      const parsed: unknown = JSON.parse(draftText);
      if (!isDraftCaseArray(parsed))
        throw new Error('Draft cases must be a JSON array of objects.');
      draftCases = parsed as ExploratoryDraftCase[];
    } catch (error) {
      notify.error(
        error instanceof Error ? error.message : 'Draft fields are invalid.',
      );
      return;
    }
    revise.mutate(
      {
        expected_revision_id: editingRevisionId,
        draft_cases: draftCases,
        manual_refs: {
          manualStoryRefs: storyRefs
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
          manualCaseRefs: caseRefs
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        },
      },
      {
        onSuccess: () => {
          notify.success('Saved as a new artifact revision.');
          setEditing(false);
        },
        onError: (error) => notify.error(error.message),
      },
    );
  };
  const confirmPublication = (): void => {
    if (draftCaseIndex == null || !confirmingRevisionId) return;
    if (!exactDestination) {
      notify.warning(
        operation === 'update'
          ? 'Choose connector, project, container, and exact case target before confirming.'
          : 'Choose connector, project, and container before confirming.',
      );
      return;
    }
    const normalizedProjectRef = projectRef.trim();
    const normalizedContainerRef = containerRef.trim();
    const normalizedCaseRef = caseRef.trim();
    confirm.mutate(
      {
        revisionId: confirmingRevisionId,
        operation,
        draftCaseIndex,
        destination: {
          connector,
          projectRef: normalizedProjectRef,
          containerRef: normalizedContainerRef,
          ...(operation === 'update' && normalizedCaseRef
            ? { caseRef: normalizedCaseRef }
            : {}),
        },
      },
      {
        onSuccess: () => {
          notify.success('Publication attempt recorded.');
          setConfirming(false);
        },
        onError: (error) => notify.error(error.message),
      },
    );
  };
  const openConfirmation = (
    draft: ExploratoryDraftCase,
    index: number,
  ): void => {
    setConfirmingRevisionId(current.id);
    setDraftCaseIndex(index);
    setProjectRef('');
    setContainerRef('');
    if (availableConnectors[0]) setConnector(availableConnectors[0]);
    if (isExploratoryAmendment(draft)) {
      setOperation('update');
      setCaseRef(draft.target.caseRef);
      setLockedCaseRef(true);
    } else {
      setOperation('create');
      setCaseRef('');
      setLockedCaseRef(false);
    }
    setConfirming(true);
  };
  const openReconciliation = (attempt: ExploratoryPublicationAttempt): void => {
    setManualOutcome('published');
    setManualExternalCaseRef('');
    setReconcilingAttempt(attempt);
  };
  const saveReconciliation = (): void => {
    if (!reconcilingAttempt) return;
    if (manualOutcome === 'published' && !manualExternalCaseRef.trim()) {
      notify.warning('Enter the external case ref you published to.');
      return;
    }
    const reconciliationOutcome =
      manualOutcome === 'published'
        ? {
            outcome: 'published' as const,
            externalCaseId: manualExternalCaseRef.trim(),
          }
        : { outcome: 'not_published' as const };
    reconcile.mutate(
      {
        attemptId: reconcilingAttempt.id,
        manualOutcome: reconciliationOutcome,
      },
      {
        onSuccess: () => {
          notify.success('Manual outcome recorded.');
          setReconcilingAttempt(null);
        },
        onError: (error) => notify.error(error.message),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Exploratory artifacts</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Local review drafts are preserved independently of connector
            availability.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={beginEdit}>
          <PencilIcon /> Edit revision
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {current.story_verdicts.length > 0 && (
          <section>
            <h3 className="text-sm font-medium">Story verdicts</h3>
            <div className="mt-2 flex flex-col gap-2">
              {current.story_verdicts.map((verdict) => (
                <div
                  key={verdict.storyRef}
                  className="rounded-md border border-border p-2 text-xs"
                >
                  <Badge variant="outline">{verdict.verdict}</Badge>{' '}
                  <span className="ml-1 font-medium">{verdict.storyRef}</span>
                  {verdict.explanation ? (
                    <p className="mt-1 text-muted-foreground">
                      {verdict.explanation}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        )}
        <section>
          <h3 className="text-sm font-medium">
            Draft cases and amendment proposals
          </h3>
          <div className="mt-2 space-y-2">
            {current.draft_cases.map((draft, index) =>
              isExploratoryAmendment(draft) ? (
                <div
                  key={index}
                  className="rounded-md border border-border p-3"
                >
                  <div className="flex justify-between gap-2">
                    <p className="text-xs font-medium">
                      Amendment to case{' '}
                      <span className="font-mono">{draft.target.caseRef}</span>
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void copy(amendmentCopyText(draft))}
                    >
                      <CopyIcon /> Copy fields
                    </Button>
                  </div>
                  <div className="mt-2">
                    <SectionLabel>Field changes</SectionLabel>
                    <div className="mt-1 space-y-1 text-[11px]">
                      {amendmentFieldDiff(draft.before, draft.after).map(
                        (field) => (
                          <div
                            key={field.key}
                            className="grid grid-cols-[auto_1fr_1fr] gap-2 rounded-sm border border-border/60 p-1.5"
                          >
                            <span className="font-medium">{field.key}</span>
                            {field.kind === 'unchanged' ? (
                              <span className="col-span-2 text-muted-foreground">
                                {formatDiffValue(field.after)}
                              </span>
                            ) : (
                              <>
                                <span
                                  className={
                                    field.kind === 'added'
                                      ? 'text-muted-foreground italic'
                                      : 'text-destructive line-through'
                                  }
                                >
                                  {field.kind === 'added'
                                    ? '(none)'
                                    : formatDiffValue(field.before)}
                                </span>
                                <span
                                  className={
                                    field.kind === 'removed'
                                      ? 'text-muted-foreground italic'
                                      : 'text-emerald-600 dark:text-emerald-400'
                                  }
                                >
                                  {field.kind === 'removed'
                                    ? '(removed)'
                                    : formatDiffValue(field.after)}
                                </span>
                              </>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="brand"
                      disabled={connectorUnavailable}
                      onClick={() => openConfirmation(draft, index)}
                    >
                      Confirm publication
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={index}
                  className="rounded-md border border-border p-3"
                >
                  <div className="flex justify-between gap-2">
                    <p className="text-xs font-medium">
                      {String(
                        draft.title ?? draft.name ?? `Draft ${index + 1}`,
                      )}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void copy(copyableDraft(draft))}
                    >
                      <CopyIcon /> Copy fields
                    </Button>
                  </div>
                  <NewCaseDraftBody draft={draft} />
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="brand"
                      disabled={connectorUnavailable}
                      onClick={() => openConfirmation(draft, index)}
                    >
                      Confirm publication
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        </section>
        <div className="rounded-md border border-dashed border-border p-3 text-xs">
          <p className="font-medium">Explicit linkage</p>
          {hasExplicitLinks ? (
            <pre className="mt-1 whitespace-pre-wrap text-muted-foreground">
              {JSON.stringify(current.explicit_links, null, 2)}
            </pre>
          ) : (
            <p className="mt-1 text-muted-foreground">
              Linkage data is unavailable. No impacted cases were inferred;
              choose manual story and case references when reviewing.
            </p>
          )}
          <p className="mt-2 text-muted-foreground">
            Manual stories:{' '}
            {current.manual_refs.manualStoryRefs.join(', ') || 'None'} · Manual
            cases: {current.manual_refs.manualCaseRefs.join(', ') || 'None'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void copy(manualCopy)}
          >
            <CopyIcon /> Copy all manual fields
          </Button>
        </div>
        {connectorUnavailable && (
          <p className="flex gap-1 text-xs text-muted-foreground">
            <AlertTriangleIcon className="size-3" />
            {connectorUnavailableMessage}
          </p>
        )}
        {artifact.publication_attempts.length > 0 && (
          <section>
            <h3 className="text-sm font-medium">Publication audit</h3>
            <div className="mt-2 space-y-2">
              {artifact.publication_attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="rounded-md border border-border p-2 text-xs"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>
                      <Badge variant="outline">{attempt.state}</Badge>{' '}
                      {attempt.operation} → {attempt.destination.connector}{' '}
                      {attempt.destination.caseRef ??
                        attempt.destination.containerRef ??
                        attempt.destination.projectRef ??
                        ''}
                    </span>
                    <span className="text-muted-foreground">
                      Attempt {attempt.attempt_number}
                    </span>
                  </div>
                  {attempt.error_message && (
                    <p className="mt-1 text-destructive">
                      {attempt.error_message}
                    </p>
                  )}
                  {canRetryExploratoryPublication(attempt.state) && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retry.isPending}
                        onClick={() =>
                          retry.mutate(attempt.id, {
                            onError: (error) => notify.error(error.message),
                          })
                        }
                      >
                        <RefreshCwIcon /> Retry
                      </Button>
                    </div>
                  )}
                  {attempt.state === 'ambiguous' && (
                    <p className="mt-2 flex gap-1 text-muted-foreground">
                      <AlertTriangleIcon className="size-3" /> Manual resolution
                      is required. Verify the exact destination before any
                      future connector action.
                    </p>
                  )}
                  {attempt.state === 'blocked' && (
                    <p className="mt-2 flex gap-1 text-muted-foreground">
                      <AlertTriangleIcon className="size-3" /> This attempt is
                      blocked and requires manual resolution.
                    </p>
                  )}
                  {isManualPublicationRequired(attempt) && (
                    <div className="mt-2 space-y-2">
                      <p className="flex gap-1 text-muted-foreground">
                        <AlertTriangleIcon className="size-3" />{' '}
                        {attempt.destination.connector} has no automated write
                        path. Copy the fields for manual entry, publish the case
                        by hand, then record what happened.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void copy(manualCopy)}
                        >
                          <CopyIcon /> Copy fields
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReconciliation(attempt)}
                        >
                          Record manual outcome
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </CardContent>
      <AppDialog
        open={editing}
        onOpenChange={setEditing}
        title="Edit exploratory artifact revision"
        description="Saving creates a new revision; it never writes to a connector."
      >
        <div className="space-y-3">
          <Label htmlFor="artifact-drafts">Draft fields (JSON)</Label>
          <Textarea
            id="artifact-drafts"
            value={draftText}
            onChange={(event) => setDraftText(event.target.value)}
            rows={12}
          />
          <Label htmlFor="artifact-stories">Manual story references</Label>
          <Input
            id="artifact-stories"
            value={storyRefs}
            onChange={(event) => setStoryRefs(event.target.value)}
            placeholder="STORY-1, STORY-2"
          />
          <Label htmlFor="artifact-cases">Manual case references</Label>
          <Input
            id="artifact-cases"
            value={caseRefs}
            onChange={(event) => setCaseRefs(event.target.value)}
            placeholder="C123, C456"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              disabled={revise.isPending}
              onClick={saveEdit}
            >
              Save new revision
            </Button>
          </div>
        </div>
      </AppDialog>
      <AppDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Confirm exact publication destination"
        description="This explicit confirmation records an auditable attempt before any connector write."
      >
        <div className="space-y-3">
          <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
            Review the exact destination below. This is not an approval of
            generated content and does not replace live HITL input.
          </p>
          {lockedCaseRef && (
            <p className="rounded-md border border-blue-500/40 bg-blue-500/5 p-2 text-xs font-medium">
              This confirms an UPDATE to existing case{' '}
              <span className="font-mono">{caseRef}</span>. The target is fixed
              by the amendment proposal and cannot be changed here.
            </p>
          )}
          <Label>Connector</Label>
          <Select
            value={connector}
            onValueChange={(value) =>
              setConnector(value as (typeof connectors)[number])
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableConnectors.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label>Operation</Label>
          <Select
            value={operation}
            disabled={lockedCaseRef}
            onValueChange={(value) =>
              setOperation(value as 'create' | 'update')
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="create">Create new case</SelectItem>
              <SelectItem value="update">Update existing case</SelectItem>
            </SelectContent>
          </Select>
          <Label htmlFor="artifact-project">Project destination</Label>
          <Input
            id="artifact-project"
            value={projectRef}
            onChange={(event) => setProjectRef(event.target.value)}
          />
          <Label htmlFor="artifact-container">
            Container / suite destination
          </Label>
          <Input
            id="artifact-container"
            value={containerRef}
            onChange={(event) => setContainerRef(event.target.value)}
          />
          <Label htmlFor="artifact-case">
            Exact case target (required for update)
          </Label>
          <Input
            id="artifact-case"
            value={caseRef}
            disabled={lockedCaseRef}
            onChange={(event) => setCaseRef(event.target.value)}
          />
          <p className="rounded-md border border-dashed border-border p-2 text-xs text-muted-foreground">
            Exact destination: {operation} · {connector} ·{' '}
            {projectRef.trim() || 'project required'} ·{' '}
            {containerRef.trim() || 'container required'}
            {operation === 'update'
              ? ` · ${caseRef.trim() || 'exact case required'}`
              : ''}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              disabled={
                connectorUnavailable ||
                confirm.isPending ||
                draftCaseIndex == null ||
                !exactDestination
              }
              onClick={confirmPublication}
            >
              Confirm {operation}
            </Button>
          </div>
        </div>
      </AppDialog>
      <AppDialog
        open={reconcilingAttempt != null}
        onOpenChange={(open) => {
          if (!open) setReconcilingAttempt(null);
        }}
        title="Record manual publication outcome"
        description="This records what you did outside the connector; the service does not verify it."
      >
        <div className="space-y-3">
          <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
            Only choose &quot;Published by hand&quot; if the case genuinely
            exists in {reconcilingAttempt?.destination.connector} with the id
            you enter below. This is you asserting the outcome, not a connector
            confirmation.
          </p>
          <Label>Outcome</Label>
          <Select
            value={manualOutcome}
            onValueChange={(value) =>
              setManualOutcome(value as 'published' | 'not_published')
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="published">
                Published by hand — I created or updated the case
              </SelectItem>
              <SelectItem value="not_published">
                Not published — abandon this attempt
              </SelectItem>
            </SelectContent>
          </Select>
          {manualOutcome === 'published' && (
            <>
              <Label htmlFor="manual-external-case-ref">
                External case ref
              </Label>
              <Input
                id="manual-external-case-ref"
                value={manualExternalCaseRef}
                onChange={(event) =>
                  setManualExternalCaseRef(event.target.value)
                }
                placeholder="C123"
              />
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setReconcilingAttempt(null)}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              disabled={reconcile.isPending}
              onClick={saveReconciliation}
            >
              Save outcome
            </Button>
          </div>
        </div>
      </AppDialog>
    </Card>
  );
}
